import React, { useEffect, useState } from "react";
import { AlertCircle, Check, CheckCircle2, RefreshCw, Settings, Wifi, WifiOff } from "lucide-react";
import type { ConnectionState } from "./types";

export const SettingsConnectionStatus: React.FC = () => {
  const [state, setState]  = useState<ConnectionState>(navigator.onLine ? "online" : "offline");
  const [since, setSince]  = useState<string>(new Date().toLocaleTimeString());
  const [apiOk, setApiOk]  = useState<boolean | null>(null);

  useEffect(() => {
    const go  = () => { setState("online");  setSince(new Date().toLocaleTimeString()); };
    const off = () => { setState("offline"); setSince(new Date().toLocaleTimeString()); };
    window.addEventListener("online",  go);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", go); window.removeEventListener("offline", off); };
  }, []);

  const checkApi = async () => {
    setState("reconnecting");
    await new Promise((r) => setTimeout(r, 1200));
    setApiOk(navigator.onLine);
    setState(navigator.onLine ? "online" : "offline");
  };

  const statusCfg = {
    online:       { color: "text-emerald-400", bg: "bg-emerald-950/30 border-emerald-800", icon: Wifi,      label: "Online"        },
    offline:      { color: "text-rose-400",    bg: "bg-rose-950/30 border-rose-800",       icon: WifiOff,   label: "Offline"       },
    reconnecting: { color: "text-amber-400",   bg: "bg-amber-950/30 border-amber-800",     icon: RefreshCw, label: "Reconnecting…" },
  };

  const cfg = statusCfg[state];

  return (
    <div className="space-y-3 text-xs font-sans">
      {/* Main Status Card */}
      <div className={`flex items-center gap-4 p-5 border rounded-xl ${cfg.bg}`}>
        <cfg.icon className={`w-7 h-7 ${cfg.color} ${state === "reconnecting" ? "animate-spin" : ""}`} />
        <div className="flex-1">
          <p className={`text-base font-bold ${cfg.color}`}>{cfg.label}</p>
          <p className="text-[10px] text-slate-500 font-mono mt-0.5">Since {since}</p>
        </div>
        <button onClick={checkApi} disabled={state === "reconnecting"}
          className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-slate-200 font-bold rounded-lg transition-colors text-[11px]">
          Check Now
        </button>
      </div>

      {/* Detail Rows */}
      <div className="p-4 bg-slate-900/80 border border-slate-800 rounded-xl space-y-2">
        <h3 className="text-[11px] font-bold font-mono text-slate-400 uppercase mb-3">Connection Details</h3>
        {[
          { label: "Network",          value: navigator.onLine ? "Connected" : "Disconnected",   ok: navigator.onLine },
          { label: "API Gateway",      value: apiOk === null ? "Not checked" : apiOk ? "Reachable" : "Unreachable", ok: apiOk ?? true },
          { label: "Settings Sync",    value: "Cached (placeholder)",          ok: true  },
          { label: "Offline Support",  value: "Available (service-worker ready)", ok: true },
          { label: "Connection Type",  value: (navigator as any).connection?.effectiveType ?? "unknown", ok: true },
          { label: "Downlink",         value: (navigator as any).connection?.downlink ? `${(navigator as any).connection.downlink} Mbps` : "unknown", ok: true },
        ].map((row) => (
          <div key={row.label} className="flex items-center justify-between p-2.5 bg-slate-950 border border-slate-800 rounded-lg">
            <span className="text-[10px] text-slate-400">{row.label}</span>
            <div className="flex items-center gap-1.5">
              {row.ok
                ? <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                : <AlertCircle  className="w-3 h-3 text-rose-400" />}
              <span className={`text-[10px] font-mono font-bold ${row.ok ? "text-emerald-300" : "text-rose-300"}`}>{row.value}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
